/**
 * The Reporting canvas as a split tree: a tile holds one panel, a split
 * holds two halves side by side ("row") or one over the other ("column"),
 * always 50/50. A tile splits across the axis its parent did not, and a
 * quarter never splits, so the canvas only ever reads as one tile, two
 * halves, a half and two quarters, or four quadrants.
 *
 * In the URL a row reads `a,b`, a column `a/b`, and a nested split sits in
 * brackets: `growth,(website/gads)`.
 */

export type Edge = "left" | "right" | "top" | "bottom";
export type Axis = "row" | "column";

export type CanvasLayout =
  | { kind: "tile"; panel: string }
  | { kind: "split"; axis: Axis; first: CanvasLayout; second: CanvasLayout };

export const MAX_TILES = 4;

/** The page's width tiers. A phone lists every panel in one column and
 *  expands the open ones in place; a tablet has the rail and a canvas of
 *  halves, since a quarter there is under about 350px; a desktop takes
 *  quadrants. */
export type Tier = "phone" | "tablet" | "desktop";

/** The narrowest window, in CSS px, of each tier above the phone. */
export const TIER_MIN_WIDTH = { tablet: 768, desktop: 1280 } as const;

export function tierOf(width: number): Tier {
  if (width >= TIER_MIN_WIDTH.desktop) return "desktop";
  return width >= TIER_MIN_WIDTH.tablet ? "tablet" : "phone";
}

/** How many tiles each tier shows. A phone holds as many open panels as a
 *  desktop, so a desktop link opens whole on it. */
export const TILE_CAP: Record<Tier, number> = { phone: MAX_TILES, tablet: 2, desktop: MAX_TILES };

export const tile = (panel: string): CanvasLayout => ({ kind: "tile", panel });

/** The panels on the canvas in tree order: a split's first half before its
 *  second, so quadrants list the left column first. */
export function panelsOf(layout: CanvasLayout | null): string[] {
  if (!layout) return [];
  return layout.kind === "tile" ? [layout.panel] : [...panelsOf(layout.first), ...panelsOf(layout.second)];
}

export const countTiles = (layout: CanvasLayout | null): number => panelsOf(layout).length;

/** The axis of the split right above a panel's tile, and how deep the tile sits. */
function placeOf(layout: CanvasLayout, panel: string, depth = 0, parent: Axis | null = null): { depth: number; parent: Axis | null } | null {
  if (layout.kind === "tile") return layout.panel === panel ? { depth, parent } : null;
  return placeOf(layout.first, panel, depth + 1, layout.axis) ?? placeOf(layout.second, panel, depth + 1, layout.axis);
}

const ROW_EDGES: Edge[] = ["left", "right"];
const COLUMN_EDGES: Edge[] = ["top", "bottom"];

/** The edges a new panel can land on beside this panel's tile: every edge
 *  of a lone tile, the two across its parent's axis for a half, none for a
 *  quarter or once the canvas holds `cap` tiles. */
export function edgesFor(layout: CanvasLayout | null, panel: string, cap = MAX_TILES): Edge[] {
  if (!layout || countTiles(layout) >= cap) return [];
  const place = placeOf(layout, panel);
  if (!place) return [];
  if (place.depth === 0) return [...ROW_EDGES, ...COLUMN_EDGES];
  if (place.depth === 1) return place.parent === "row" ? COLUMN_EDGES : ROW_EDGES;
  return [];
}

export type PlaceResult =
  | { ok: true; layout: CanvasLayout }
  | { ok: false; reason: "full" | "present" | "edge" };

/** The canvas with `panel` added: on an empty canvas it fills it, else it
 *  splits `at.target`'s tile and lands on `at.edge`. */
export function place(layout: CanvasLayout | null, panel: string, at: { target: string; edge: Edge } | null, cap = MAX_TILES): PlaceResult {
  if (!layout) return { ok: true, layout: tile(panel) };
  if (panelsOf(layout).includes(panel)) return { ok: false, reason: "present" };
  if (countTiles(layout) >= cap) return { ok: false, reason: "full" };
  if (!at || !edgesFor(layout, at.target, cap).includes(at.edge)) return { ok: false, reason: "edge" };
  const { target, edge } = at;
  const split = (node: CanvasLayout): CanvasLayout => {
    if (node.kind === "split") return { ...node, first: split(node.first), second: split(node.second) };
    if (node.panel !== target) return node;
    const axis: Axis = edge === "left" || edge === "right" ? "row" : "column";
    const before = edge === "left" || edge === "top";
    return { kind: "split", axis, first: before ? tile(panel) : node, second: before ? node : tile(panel) };
  };
  return { ok: true, layout: split(layout) };
}

/** `place` with no edge picked, for a phone, where a tap opens a panel in
 *  place: the new tile goes after the last tile in reading order that can
 *  split, on its right edge, or its bottom for a half in a row. So a layout
 *  built on a phone is a well formed canvas on a desktop. */
export function autoPlace(layout: CanvasLayout | null, panel: string, cap = MAX_TILES): PlaceResult {
  if (!layout) return { ok: true, layout: tile(panel) };
  const target = [...readingOrder(layout)].reverse().find((p) => edgesFor(layout, p, cap).length > 0);
  if (!target) return place(layout, panel, null, cap);
  const edge: Edge = edgesFor(layout, target, cap).includes("right") ? "right" : "bottom";
  return place(layout, panel, { target, edge }, cap);
}

/** The canvas with `panel` in `target`'s tile instead, the split unchanged.
 *  The layout as it was when `target` is not on it or `panel` already is. */
export function replace(layout: CanvasLayout | null, target: string, panel: string): CanvasLayout | null {
  if (!layout || panelsOf(layout).includes(panel)) return layout;
  if (layout.kind === "tile") return layout.panel === target ? tile(panel) : layout;
  return { ...layout, first: replace(layout.first, target, panel) ?? layout.first, second: replace(layout.second, target, panel) ?? layout.second };
}

/** The canvas without `panel`: its sibling takes the whole of their split.
 *  Null once the last tile closes. */
export function remove(layout: CanvasLayout | null, panel: string): CanvasLayout | null {
  if (!layout) return null;
  if (layout.kind === "tile") return layout.panel === panel ? null : layout;
  const first = remove(layout.first, panel);
  const second = remove(layout.second, panel);
  if (!first) return second;
  if (!second) return first;
  return { ...layout, first, second };
}

/** A tile's box as fractions of the canvas, 0 to 1. */
export interface TileRect {
  panel: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Each tile's box. The page draws tiles as siblings placed by these, so a
 *  split never remounts the panels already open. */
export function rectsOf(layout: CanvasLayout | null, box: Omit<TileRect, "panel"> = { x: 0, y: 0, width: 1, height: 1 }): TileRect[] {
  if (!layout) return [];
  if (layout.kind === "tile") return [{ panel: layout.panel, ...box }];
  const half =
    layout.axis === "row"
      ? [{ ...box, width: box.width / 2 }, { ...box, x: box.x + box.width / 2, width: box.width / 2 }]
      : [{ ...box, height: box.height / 2 }, { ...box, y: box.y + box.height / 2, height: box.height / 2 }];
  return [...rectsOf(layout.first, half[0]), ...rectsOf(layout.second, half[1])];
}

/** The panels as a person reads the canvas: top row first, left to right
 *  in each row, so quadrants read the top two before the bottom two. */
export function readingOrder(layout: CanvasLayout | null): string[] {
  return rectsOf(layout)
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((r) => r.panel);
}

/** What a tier with room for `cap` tiles shows of a layout: its first `cap`
 *  panels in reading order, with the split between them kept, and the
 *  panels past them. The layout itself is never cut, so a wider screen
 *  shows the hidden panels again. */
export function visibleLayout(layout: CanvasLayout | null, cap: number): { layout: CanvasLayout | null; hidden: string[] } {
  const hidden = readingOrder(layout).slice(cap);
  return { layout: hidden.reduce<CanvasLayout | null>(remove, layout), hidden };
}

const SEPARATOR: Record<Axis, string> = { row: ",", column: "/" };

export function serialize(layout: CanvasLayout | null): string {
  if (!layout) return "";
  if (layout.kind === "tile") return layout.panel;
  const part = (node: CanvasLayout) => (node.kind === "split" ? `(${serialize(node)})` : serialize(node));
  return `${part(layout.first)}${SEPARATOR[layout.axis]}${part(layout.second)}`;
}

/** Whether a tree keeps the canvas's shapes: each split across its parent's
 *  axis, no deeper than quadrants, every panel once. */
function wellFormed(layout: CanvasLayout): boolean {
  const panels = panelsOf(layout);
  if (new Set(panels).size !== panels.length || panels.length > MAX_TILES) return false;
  const check = (node: CanvasLayout, depth: number, parent: Axis | null): boolean => {
    if (node.kind === "tile") return true;
    if (depth >= 2 || node.axis === parent) return false;
    return check(node.first, depth + 1, node.axis) && check(node.second, depth + 1, node.axis);
  };
  return check(layout, 0, null);
}

/** Reads `serialize`'s text back. Null for an empty canvas; "invalid" for
 *  text that is not a canvas of known panels, which the page answers with
 *  its default. */
export function parse(text: string, known: (panel: string) => boolean): CanvasLayout | null | "invalid" {
  const source = text.replace(/\s+/g, "");
  if (source === "") return null;
  let i = 0;
  const node = (): CanvasLayout | null => {
    const left = operand();
    if (!left) return null;
    const sep = source[i];
    if (sep !== "," && sep !== "/") return left;
    i += 1;
    const right = operand();
    if (!right) return null;
    return { kind: "split", axis: sep === "," ? "row" : "column", first: left, second: right };
  };
  const operand = (): CanvasLayout | null => {
    if (source[i] === "(") {
      i += 1;
      const inner = node();
      if (!inner || source[i] !== ")") return null;
      i += 1;
      return inner;
    }
    const match = /^[a-z][a-z0-9]*/.exec(source.slice(i));
    if (!match || !known(match[0])) return null;
    i += match[0].length;
    return tile(match[0]);
  };
  const layout = node();
  if (!layout || i !== source.length || !wellFormed(layout)) return "invalid";
  return layout;
}
